import {
  Icon,
  ModalContext,
  Spinner,
  toast,
  useCheckIfSubscriptionFeatureActiveBroker,
  ViewerContext,
} from "dash-ui";

import {
  getProductNameSlug,
  isHigherPricePerMonth,
  isLowerPricePerMonth,
} from "common/helpers";
import { useRecurringType } from "common/hooks/useRecurringType/useRecurringType";
import {
  ProductSubscriptionStatusesEnum,
  SubscriptionProductNamesEnum,
} from "dash-common";
import { RoutesPath } from "flow-components/routes/routes";
import {
  RecurringTypesEnum,
  SubscriptionProduct,
  useCancelScheduledDowngradeMutation,
  useCreateCheckoutSessionMutation,
  useGetAppBasicInfoQuery,
  useGetSubscriptionProductsQuery,
} from "gql/generated.graphql";
import { ProfileTabsKeys } from "pages/ProfilePage/ProfilePage.constants";
import { useContext } from "react";
import { Navigate } from "react-router-dom";
import SubscriptionProductCard from "v2/components/billing/SubscriptionProductCard/SubscriptionProductCard";
import {
  InvoicePreviewModal,
  LinkToCustomerPortal,
  WarningBanner,
} from "./components/";
import {
  CancelTrialModal,
  RecurringTypeToggle,
} from "./components/SubscriptionProductsSection/components";
import { formatDateString } from "./ProfileBilling.helpers";
import { ProfileBillingContainer } from "./ProfileBilling.styles";

export default function ProfileBilling() {
  const { isSubscriptionActive } = useCheckIfSubscriptionFeatureActiveBroker();
  const { subscription } = useContext(ViewerContext);
  const currentUserProduct = subscription?.subscriptionProduct;

  const { toggleModal, setPayload } = useContext(ModalContext);

  const { data: pluginUserData, loading: pluginUserQueryLoading } =
    useGetAppBasicInfoQuery();

  const isApiUser = !!pluginUserData?.getApiCredentialsBasicInfo;

  const { data, loading } = useGetSubscriptionProductsQuery({
    skip: pluginUserQueryLoading,
    variables: {
      filter: {
        isApiUser: isApiUser,
      },
    },
  });
  const products = data?.getSubscriptionProducts;

  const { recurringType, setRecurringType } = useRecurringType();

  const [cancelScheduledDowngrade, { loading: cancelScheduleLoading }] =
    useCancelScheduledDowngradeMutation();

  const [createCheckoutSession, { loading: checkoutLoading }] =
    useCreateCheckoutSessionMutation();

  function handleRenewPlan() {
    cancelScheduledDowngrade({
      onCompleted: () => {
        toast.success("Your plan was renewed!");
      },
      onError: () => {
        toast.error("There was an error renewing your plan.");
      },
    });
  }

  async function handleSelectPlan(
    product: SubscriptionProduct,
    withTrial?: boolean
  ) {
    // Create a new subscription (in case is a trial or basic -> paid)
    if (!currentUserProduct || currentUserProduct?.price === 0 || withTrial) {
      const productSlug = getProductNameSlug(product);
      const data = await createCheckoutSession({
        variables: {
          input: {
            priceId: product.stripePriceId,
            productId: product.stripeProductId,
            successUrl: `${window.location.href}&subscription=${productSlug}`,
            cancelUrl: window.location.href,
          },
        },
      });

      const sessionUrl = data.data?.createCheckoutSession.sessionUrl;

      return window.open(sessionUrl, "_self");
    }

    const isCurrent = product.name === currentUserProduct?.name;

    // Update an existing subscription
    let mode: "downgrade" | "upgrade" = "upgrade";
    if (product.name === SubscriptionProductNamesEnum.API) {
      mode = "upgrade";
    } else if (isHigherPricePerMonth(product, currentUserProduct)) {
      mode = isCurrent ? "downgrade" : "upgrade";
    } else if (isLowerPricePerMonth(product, currentUserProduct)) {
      mode = isCurrent ? "upgrade" : "downgrade";
    }

    setPayload({
      newSubscriptionProductId: product._id,
      newStripeProductId: product.stripeProductId,
      newStripePriceId: product.stripePriceId,
      mode,
    });
    toggleModal(true, "InvoicePreviewModal");
  }

  if (!isSubscriptionActive) {
    return (
      <Navigate
        to={`${RoutesPath.Profile}?tab=${ProfileTabsKeys.ACCOUNT_INFO}`}
      />
    );
  }

  return (
    <>
      <ProfileBillingContainer
        w="100%"
        h={"100%"}
        flexDirection="column"
        alignItems="center"
        justifyContent="flex-start"
        gap={16}
      >
        {/* Failed payment */}
        {(subscription?.status === ProductSubscriptionStatusesEnum.PAST_DUE ||
          subscription?.status ===
            ProductSubscriptionStatusesEnum.INCOMPLETE) && (
          <WarningBanner>
            ⚠️ There was a problem with your last payment. Please visit your{" "}
            <LinkToCustomerPortal>customer portal</LinkToCustomerPortal>
          </WarningBanner>
        )}

        {/* Scheduled downgrade */}
        {subscription?.schedule && (
          <WarningBanner>
            ⚠️ Your subscription is scheduled to downgrade on{" "}
            {formatDateString(subscription.schedule.nextPhaseStart)}.{" "}
            <span
              onClick={handleRenewPlan}
              className="font-semibold hover:opacity-90 active:opacity-80 cursor-pointer"
            >
              Renew your plan
            </span>{" "}
            and keep enjoying DASH's full benefits!
          </WarningBanner>
        )}

        {/* Scheduled cancellation */}
        {subscription?.status ===
          ProductSubscriptionStatusesEnum.SCHEDULED_CANCELLATION && (
          <WarningBanner>
            ⚠️ Your subscription will be canceled on{" "}
            {formatDateString(subscription.currentPeriodEndAt)}.{" "}
            <LinkToCustomerPortal>Renew your plan</LinkToCustomerPortal> and
            keep enjoying DASH's full benefits!
          </WarningBanner>
        )}

        {cancelScheduleLoading && <Spinner />}

        {/* Subscription Cards */}
        <RecurringTypeToggle
          recurringType={recurringType}
          onToggle={(newRecurringType) => setRecurringType(newRecurringType)}
          className="mt-2"
        />
        <div className="w-full flex flex-col gap-4 flex-grow flex-wrap justify-center items-center xl:flex-row xl:items-stretch">
          {loading && <Spinner minHeight={512} />}
          {products &&
            products
              .filter((product) => {
                return (
                  product.recurringType === recurringType ||
                  product.recurringType === RecurringTypesEnum.FREE
                );
              })
              .map((product) => (
                <SubscriptionProductCard
                  key={product._id}
                  product={product as SubscriptionProduct}
                  onSelectPlan={handleSelectPlan}
                  loading={checkoutLoading}
                  isCurrent={
                    product._id === subscription?.subscriptionProductId
                  }
                  className="xl:max-w-[360px]"
                />
              ))}
        </div>
        <div className="flex justify-center items-center gap-1">
          <Icon name="infoCircle" fill="#94a3b8" color="#94a3b8" />
          <a
            href="https://help.dashtodash.com"
            target="_blank"
            className="text-sm text-slate-400"
          >
            I have a billing problem
          </a>
        </div>
      </ProfileBillingContainer>
      <InvoicePreviewModal />
      <CancelTrialModal />
    </>
  );
}
